import { useState, useEffect } from 'react';
import {
  UsersIconFilled,
  PhoneIcon,
  EnvelopIcon,
  HyperLinkIcon,
} from '@rsos/assets/icons';
import { Box, Text } from '@rsos/base-components';
import MultiEntryInput from '@rsos/components/capstone/base/form/inputs/MultiEntryInput/MultiEntryInput';
import useHasAccessToFieldShareIar from '../../../../hooks/useHasAccessToFieldShareIar';
import type { Checkbox } from '../../constants/ShareEvent.types';
import {
  FieldSharingMethod,
  FIELD_DIRECT_RECIPIENTS_SHARE,
  FIELD_SHARE_LINK_SHARE,
  FIELD_IAR_SHARE,
} from '../../constants/shareMethods';
import ShareEventCheckbox from '../common/ShareEventCheckbox';
import IarIcon from './IarIcon';

export interface MultiInputTag {
  id: string;
  value: string;
  isValid: boolean;
  type?: string;
  error?: string;
}

interface ShareEventRecipientsProps {
  onRecipientsChange: (recipients: MultiInputTag[]) => void;
  onSharingMethodChange: (method: FieldSharingMethod) => void;
  initialRecipients?: MultiInputTag[];
  selectedSharingMethod?: FieldSharingMethod;
  error?: string;
  isDisabled?: boolean;
}

const CustomEnvelopIcon = () => <EnvelopIcon />;

const CustomPhoneIcon = () => (
  <PhoneIcon viewboxWidth={12} viewboxHeight={14} />
);

const ShareEventRecipients = ({
  onRecipientsChange,
  onSharingMethodChange,
  initialRecipients = [],
  selectedSharingMethod = 'direct-recipients',
  error,
  isDisabled = false,
}: ShareEventRecipientsProps) => {
  const showIarOption = useHasAccessToFieldShareIar();

  const [checkboxes, setCheckboxes] = useState<Checkbox[]>([
    {
      label: 'Direct Recipients',
      description:
        'Add email and/or phone numbers to directly send to recipients',
      isChecked: isDisabled
        ? false
        : selectedSharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE,
      id: FIELD_DIRECT_RECIPIENTS_SHARE,
      icon: UsersIconFilled,
      disabled: isDisabled,
    },
    {
      label: 'Share via IamResponding',
      description:
        'Share to agencies and personnel directly through IamResponding',
      isChecked: isDisabled ? false : selectedSharingMethod === FIELD_IAR_SHARE,
      id: FIELD_IAR_SHARE,
      icon: IarIcon,
      disabled: isDisabled,
    },
    {
      label: 'Share Link',
      description:
        'Generate a unique link that you can share out to recipients',
      isChecked: isDisabled
        ? false
        : selectedSharingMethod === FIELD_SHARE_LINK_SHARE,
      id: FIELD_SHARE_LINK_SHARE,
      icon: HyperLinkIcon,
      disabled: isDisabled,
    },
  ]);

  const [mixedTags, setMixedTags] =
    useState<MultiInputTag[]>(initialRecipients);
  const [formValues, setFormValues] = useState({
    mixed: '',
    mixedCumulative: initialRecipients,
  });

  // Sync internal state when initial recipients change
  useEffect(() => {
    setMixedTags(initialRecipients);
    setFormValues(prev => ({ ...prev, mixedCumulative: initialRecipients }));
  }, [initialRecipients]);

  // Sync checkbox state when sharing method or disabled state changes
  useEffect(() => {
    setCheckboxes(prev =>
      prev.map(checkbox => ({
        ...checkbox,
        isChecked: isDisabled ? false : checkbox.id === selectedSharingMethod,
        disabled: isDisabled,
      })),
    );
  }, [selectedSharingMethod, isDisabled]);

  const handleChange = (id: string) => () => {
    const newMethod = id as FieldSharingMethod;

    setCheckboxes((prev: Checkbox[]) =>
      prev.map((checkbox: Checkbox) => ({
        ...checkbox,
        isChecked: checkbox.id === id,
      })),
    );

    // Notify parent of sharing method change
    onSharingMethodChange(newMethod);
  };

  const handleTagsUpdate = (newTags: MultiInputTag[]) => {
    setMixedTags(newTags);
    onRecipientsChange(newTags); // Notify parent of changes
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormValues(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSetFieldValue = (field: string, value: unknown) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
  };

  const selectedCheckbox = checkboxes.find((cb: Checkbox) => cb.isChecked);

  const visibleCheckboxes = showIarOption
    ? checkboxes
    : checkboxes.filter(c => c.id !== FIELD_IAR_SHARE);

  return (
    <Box opacity={isDisabled ? 0.5 : 1}>
      {visibleCheckboxes.map((checkbox: Checkbox) => (
        <ShareEventCheckbox
          checkbox={checkbox}
          onChange={handleChange(checkbox.id)}
          key={checkbox.label}
        />
      ))}
      {!isDisabled &&
        selectedCheckbox?.id === FIELD_DIRECT_RECIPIENTS_SHARE && (
          <>
            <Text
              color="secondaryText"
              size="small"
              fontWeight="bold"
              my="8px"
              px="5px"
            >
              Recipients
            </Text>
            <MultiEntryInput
              inputType="mixed"
              value={formValues.mixed}
              name="mixed"
              placeholder="Add emails or phone numbers..."
              hiddenValue={formValues.mixedCumulative}
              hiddenInputName="mixedCumulative"
              tags={mixedTags}
              updateTags={handleTagsUpdate}
              setFieldValue={handleSetFieldValue}
              handleChange={handleInputChange}
              validator={() => true}
              error={error}
              icons={{ email: CustomEnvelopIcon, phone: CustomPhoneIcon }}
            />
          </>
        )}
      {!isDisabled && selectedCheckbox?.id === FIELD_SHARE_LINK_SHARE && (
        <Text color="secondaryText" size="normal" mt="8px" px="5px">
          Generate and copy a share link. Anyone with the link can view event
          data. The link will expire in 1 hour.
        </Text>
      )}
    </Box>
  );
};

export default ShareEventRecipients;
